import {Text} from '@smart/react-components';
import {Button, Icon, Notification} from '@smart/react-components';
import type {FormEventHandler} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {availableContactOptions} from '../../../bff/post-form/mulesoft/types';
import FormStyles from '../../common/form-styles.module.scss';
import {scrollToRef} from '../../common/helpers';
import type {ViewsFetchingToPostForm} from '../../common/types';
import {AppModelContext} from '../../components/app';
import Fields from '../../components/easy-form/fields';
import type {WrapperHandler} from '../../components/easy-form/types';
import PhoneCodeSelect from '../../components/phone-code-select';
import PhoneField from '../../components/phone-field';
import {useAPI} from '../../hooks/use-api';
import {useEntry} from '../../hooks/use-entry';
import {useTracking} from '../../hooks/use-tracking';
import type {ITrackContactPrefs} from '../../hooks/use-tracking/types';
import {useViews} from '../../hooks/use-views';
import type {Success} from '../success';

export interface IContactPrefsState {
  contactMethod: Array<(typeof availableContactOptions)[number]>;
  phoneNumber: string;
  phoneCode: string;
  phone?: boolean;
  sms?: boolean;
}

const viewName: ViewsFetchingToPostForm = `contact-prefs`;

export default function ContactPrefs(): JSX.Element {
  const [hasErrors, setHasErrors] = useState(true);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [formData, setFormData] = useState<IContactPrefsState>();
  const [loading, setLoading] = useState(false);
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const apiFetch = useAPI(viewName, true);
  const {setView} = useViews();
  const [entry] = useEntry();
  const [trackStep, successTrack] = useTracking<ITrackContactPrefs>(
    `newsletter_rms`,
    `contact-prefs`,
  );
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const cmsTranslations =
    React.useContext(AppModelContext).cms.formsFeatureAppReserveMySeatUi || {};
  const apiTranslations =
    React.useContext(AppModelContext).cms.apiErrorMessageKeys || {};
  // UPDATE WHEN ERROR CODES ARE AVAILABLE FROM API
  // const cmsTranslationsErrors =
  //     React.useContext(AppModelContext).cms.formsFeatureAppValidation_ErrorsUi ||
  //     {};
  //   const cfmError = cmsTranslationsErrors.validations_serverError?.toString();

  useEffect(() => {
    if (entry === viewName) {
      trackStep(1, `form_start`);
    }
  }, []);

  const onSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (hasErrors) {
      setSubmitAttempt(true);
      scrollToRef(formWrapperRef);
      return;
    }

    if (!formData) return;

    setLoading(true);
    setServerError(undefined);

    try {
      trackStep(3, `form_engagement`);
      await apiFetch(formData);
      successTrack(5, `success-contact-prefs`);

      if (entry === `newsletter-form`) {
        setView(`content-prefs`);
      } else {
        setView<typeof Success>(`success`, {
          successGreeting: cmsTranslations.contactprefs_successPage_greeting,
          successTitle: cmsTranslations.contactprefs_successPage_title,
        });
      }
    } catch (error) {
      setServerError(error as string);
      setLoading(false);
      scrollToRef(formWrapperRef);
    }
  };

  const changeHandler: WrapperHandler<IContactPrefsState> = (state, errors) => {
    if (state.sms || state.phone) {
      setPhoneRequired(true);
    } else if (!(state.sms || state.phone)) {
      setPhoneRequired(false);
    } else {
    }

    setHasErrors(!!errors);
    setFormData(state as unknown as IContactPrefsState);
  };
  const setValueFromAEM = () => {
    let apiErrorMessage = ``;
    let translatedString;

    const getStringFromAEM = apiTranslations.errorMessageKeys?.filter((e) => {
      return e?.includes(serverError as any);
    }, serverError);

    if (!getStringFromAEM) {
      apiErrorMessage = apiTranslations.generalFallbackErrorMessage as string;
    } else {
      translatedString = getStringFromAEM && getStringFromAEM[0]?.split(`:`);
      apiErrorMessage = translatedString && (translatedString[1] as any);
    }
    return apiErrorMessage;
  };

  return (
    <div className={FormStyles.wrapper} ref={formWrapperRef}>
      <Icon
        className={FormStyles.centerIcon}
        icon="successful-checkmark"
        mode={300}
      />

      <Text className={FormStyles.heading} variant="hl-200" as="p">
        {cmsTranslations.contactprefs_callToAction}
      </Text>

      <Text className={FormStyles.upperText} variant="p-100" as="p">
        {cmsTranslations.contactprefs_title}
      </Text>

      {serverError && (
        <Notification
          signal="error"
          className={FormStyles.serverError}
          onOpenChange={(open) => {
            if (!open) {
              setServerError(undefined);
            }
          }}
        >
          <Notification.Text>{setValueFromAEM()}</Notification.Text>
        </Notification>
      )}

      <form onSubmit={onSubmit}>
        <Fields.Wrapper
          showErrors={submitAttempt}
          disabled={loading}
          translationPrefix={`${viewName}.contactMethod`}
          onUpdate={changeHandler}
        >
          <Text className={FormStyles.upperText} variant="p-300" as="p">
            {cmsTranslations.contactprefs_legend_contactMethod}
          </Text>
          <Fields.Group>
            <Fields.Checkbox
              props={{disabled: true}}
              key="email"
              tag="email"
              initial={true}
              label={cmsTranslations.contactprefs_contactMethod_email}
            />
            <Fields.Checkbox
              key="sms"
              tag="sms"
              label={cmsTranslations.contactprefs_contactMethod_sms}
            />
            <Fields.Checkbox
              key="phone"
              tag="phone"
              label={cmsTranslations.contactprefs_contactMethod_phone}
            />
            {formData && (formData[`sms`] || formData[`phone`]) && (
              <div className={FormStyles.phoneFieldContainer}>
                <PhoneCodeSelect
                  required={phoneRequired}
                  tag="phoneCode"
                  props={{className: FormStyles.phoneCode}}
                  label={cmsTranslations.contactprefs_contactMethod_phoneCode}
                />
                <PhoneField
                  required={phoneRequired}
                  tag="phoneNumber"
                  props={{className: FormStyles.phoneNumber}}
                  label={cmsTranslations.contactprefs_contactMethod_phoneNumber}
                />
              </div>
            )}
          </Fields.Group>

          <Button
            type="submit"
            variant="primary"
            className={FormStyles.button}
            disabled={loading}
          >
            {cmsTranslations.contactprefs_submit || `Submit`}
          </Button>
        </Fields.Wrapper>
      </form>
    </div>
  );
}
